import React from 'react';
import { View, Text, StyleSheet, Platform } from 'react-native';
import { useTheme } from '../context/ThemeContext';

/**
 * MarkdownRenderer - Renders markdown content with proper formatting
 * Supports: Headers, Bold, Lists, Checkboxes, Horizontal Rules, Code blocks
 */
const MarkdownRenderer = ({ content, style, customColors }) => {
  const { palette } = useTheme();
  
  // Use custom colors if provided, otherwise fall back to theme palette
  const colors = customColors || palette;

  if (!content) return null;

  // Parse markdown into structured elements
  const parseMarkdown = (text) => {
    const lines = text.split('\n');
    const elements = [];
    let currentList = null;
    let listItems = [];

    lines.forEach((line, index) => {
      // Empty lines
      if (line.trim() === '') {
        if (currentList) {
          elements.push({ type: 'list', items: [...listItems], listType: currentList });
          currentList = null;
          listItems = [];
        }
        elements.push({ type: 'spacer', key: `spacer-${index}` });
        return;
      }

      // Headers (# ## ###)
      const headerMatch = line.match(/^(#{1,6})\s+(.+)$/);
      if (headerMatch) {
        if (currentList) {
          elements.push({ type: 'list', items: [...listItems], listType: currentList });
          currentList = null;
          listItems = [];
        }
        elements.push({
          type: 'header',
          level: headerMatch[1].length,
          text: headerMatch[2],
          key: `header-${index}`,
        });
        return;
      }

      // Horizontal rule
      if (line.match(/^---+$/)) {
        if (currentList) {
          elements.push({ type: 'list', items: [...listItems], listType: currentList });
          currentList = null;
          listItems = [];
        }
        elements.push({ type: 'hr', key: `hr-${index}` });
        return;
      }

      // Checkbox list items
      const checkboxMatch = line.match(/^-\s+\[([ x])\]\s+(.+)$/);
      if (checkboxMatch) {
        if (currentList !== 'checkbox') {
          if (currentList) {
            elements.push({ type: 'list', items: [...listItems], listType: currentList });
          }
          currentList = 'checkbox';
          listItems = [];
        }
        listItems.push({
          checked: checkboxMatch[1] === 'x',
          text: checkboxMatch[2],
          key: `checkbox-${index}`,
        });
        return;
      }

      // Bullet list items (• or - or *)
      const bulletMatch = line.match(/^[•\-*]\s+(.+)$/);
      if (bulletMatch) {
        if (currentList !== 'bullet') {
          if (currentList) {
            elements.push({ type: 'list', items: [...listItems], listType: currentList });
          }
          currentList = 'bullet';
          listItems = [];
        }
        listItems.push({
          text: bulletMatch[1],
          key: `bullet-${index}`,
        });
        return;
      }

      // Numbered list items
      const numberedMatch = line.match(/^(\d+)\.\s+(.+)$/);
      if (numberedMatch) {
        if (currentList !== 'numbered') {
          if (currentList) {
            elements.push({ type: 'list', items: [...listItems], listType: currentList });
          }
          currentList = 'numbered';
          listItems = [];
        }
        listItems.push({
          number: numberedMatch[1],
          text: numberedMatch[2],
          key: `numbered-${index}`,
        });
        return;
      }

      // Regular paragraph
      if (currentList) {
        elements.push({ type: 'list', items: [...listItems], listType: currentList });
        currentList = null;
        listItems = [];
      }
      elements.push({
        type: 'paragraph',
        text: line,
        key: `p-${index}`,
      });
    });

    // Add any remaining list
    if (currentList) {
      elements.push({ type: 'list', items: [...listItems], listType: currentList });
    }

    return elements;
  };

  // Format inline markdown (bold, italic, code)
  const formatInline = (text) => {
    const parts = [];
    let currentIndex = 0;

    // Match bold (**text**)
    const boldRegex = /\*\*(.+?)\*\*/g;
    let match;

    const processedText = text.replace(boldRegex, (_, content) => `<BOLD>${content}</BOLD>`);
    const segments = processedText.split(/(<BOLD>.*?<\/BOLD>)/);

    return segments.map((segment, idx) => {
      if (segment.startsWith('<BOLD>')) {
        const boldText = segment.replace(/<\/?BOLD>/g, '');
        return (
          <Text key={`bold-${idx}`} style={styles.bold}>
            {boldText}
          </Text>
        );
      }
      return <Text key={`text-${idx}`}>{segment}</Text>;
    });
  };

  const elements = parseMarkdown(content);

  return (
    <View style={[styles.container, style]}>
      {elements.map((element) => {
        switch (element.type) {
          case 'header':
            const headerStyle = [
              styles.header,
              element.level === 1 && styles.h1,
              element.level === 2 && styles.h2,
              element.level === 3 && styles.h3,
              { color: colors.text || 'white' },
            ];
            return (
              <Text key={element.key} style={headerStyle}>
                {formatInline(element.text)}
              </Text>
            );

          case 'paragraph':
            return (
              <Text
                key={element.key}
                style={[styles.paragraph, { color: colors.mutedText || 'rgba(255,255,255,0.8)' }]}
              >
                {formatInline(element.text)}
              </Text>
            );

          case 'list':
            return (
              <View key={`list-${element.items[0]?.key}`} style={styles.list}>
                {element.items.map((item) => {
                  if (element.listType === 'checkbox') {
                    return (
                      <View key={item.key} style={styles.listItem}>
                        <Text style={[styles.checkbox, { color: colors.text }]}>
                          {item.checked ? '✅' : '☐'}
                        </Text>
                        <Text
                          style={[
                            styles.listItemText,
                            { color: colors.mutedText || 'rgba(255,255,255,0.8)' },
                          ]}
                        >
                          {formatInline(item.text)}
                        </Text>
                      </View>
                    );
                  } else if (element.listType === 'numbered') {
                    return (
                      <View key={item.key} style={styles.listItem}>
                        <Text style={[styles.bullet, { color: colors.primary || '#FF0600' }]}>
                          {item.number}.
                        </Text>
                        <Text
                          style={[
                            styles.listItemText,
                            { color: colors.mutedText || 'rgba(255,255,255,0.8)' },
                          ]}
                        >
                          {formatInline(item.text)}
                        </Text>
                      </View>
                    );
                  } else {
                    return (
                      <View key={item.key} style={styles.listItem}>
                        <Text style={[styles.bullet, { color: colors.primary || '#FF0600' }]}>
                          •
                        </Text>
                        <Text
                          style={[
                            styles.listItemText,
                            { color: colors.mutedText || 'rgba(255,255,255,0.8)' },
                          ]}
                        >
                          {formatInline(item.text)}
                        </Text>
                      </View>
                    );
                  }
                })}
              </View>
            );

          case 'hr':
            return (
              <View
                key={element.key}
                style={[styles.hr, { backgroundColor: colors.border || 'rgba(255,255,255,0.12)' }]}
              />
            );

          case 'spacer':
            return <View key={element.key} style={styles.spacer} />;

          default:
            return null;
        }
      })}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    fontFamily: 'Archivo',
    fontWeight: '700',
    marginBottom: 12,
    marginTop: 8,
            ...Platform.select({
          web: {
            textShadow: '3px 3px 0px rgba(0,0,0,1)',
          },
        }),
  },
  h1: {
    fontSize: 28,
    marginTop: 16,
    marginBottom: 16,
            ...Platform.select({
          web: {
            textShadow: '3px 3px 0px rgba(0,0,0,1)',
          },
        }),
  },
  h2: {
    fontSize: 22,
    marginTop: 20,
    marginBottom: 12,
            ...Platform.select({
          web: {
            textShadow: '1px 1px 0px rgba(0,0,0,1)',
          },
        }),
  },
  h3: {
    fontSize: 18,
    marginTop: 16,
    marginBottom: 8,
            ...Platform.select({
          web: {
            textShadow: '1px 1px 0px rgba(0,0,0,1)',
          },
        }),
  },
  paragraph: {
    fontFamily: 'Archivo',
    fontSize: 15,
    lineHeight: 24,
    marginBottom: 12,
            ...Platform.select({
      web: {
        textShadow: '1px 1px 0px rgba(0,0,0,1)',
      },
    }),
  },
  bold: {
    fontWeight: '700',
  },
  list: {
    marginBottom: 16,
    marginLeft: 8,
            ...Platform.select({
      web: {
        textShadow: '1px 1px 0px rgba(0,0,0,1)',
      },
    }),
  },
  listItem: {
    flexDirection: 'row',
    marginBottom: 8,
    alignItems: 'flex-start',
  },
  bullet: {
    fontFamily: 'Archivo',
    fontSize: 15,
    fontWeight: '700',
    marginRight: 12,
    marginTop: 2,
    minWidth: 20,
  },
  checkbox: {
    fontSize: 16,
    marginRight: 12,
    marginTop: 2,
  },
  listItemText: {
    fontFamily: 'Archivo',
    fontSize: 15,
    lineHeight: 24,
    flex: 1,
  },
  hr: {
    height: 1,
    marginVertical: 20,
  },
  spacer: {
    height: 8,
  },
});

export default MarkdownRenderer;

